/**
 * 생성기 턴의 결과 해석 — respond · terminal error(스트림 전 HTTP 거절 포함).
 * 생성기가 실제로 읽는 필드(result_summary · usage)만 검증하고, 에러 본문은 관대하게
 * 읽는다 — 모르는 reason 이 와도 사용자 실패로 만들지 않는다.
 */
import { z } from 'zod';

const ResultSummarySchema = z
  .object({
    type: z.literal('generation'),
    requested_count: z.number().int().nonnegative(),
    created_count: z.number().int().nonnegative(),
    shortfall_count: z.number().int().nonnegative(),
    shortfall_reason: z.string(),
  })
  .passthrough();

const UsageSchema = z
  .object({
    state: z.enum(['normal', 'low', 'blocked']),
    remaining: z.number().int(),
    limit: z.number().int(),
  })
  .passthrough();

const GeneratorRespondSchema = z
  .object({
    type: z.literal('respond'),
    result_summary: ResultSummarySchema.nullish(),
    usage: UsageSchema.nullish(),
  })
  .passthrough();

export type AiGenerateDockUsage = z.infer<typeof UsageSchema>;

export type AiGenerateDockOutcome =
  | { kind: 'success'; created: number }
  | { kind: 'partial'; created: number; failed: number }
  | { kind: 'failed' }
  /** 생성까지 가지 못하고 끝난 턴 — 자료 확인 실패 등. 입력을 유지한다. */
  | { kind: 'not_generated' }
  | { kind: 'invalid_response' };

export type AiGenerateDockRespondResult = {
  outcome: AiGenerateDockOutcome;
  usage: AiGenerateDockUsage | null;
};

export const resolveAiGenerateDockRespond = (
  respond: unknown,
): AiGenerateDockRespondResult => {
  const parsed = GeneratorRespondSchema.safeParse(respond);
  if (!parsed.success) {
    return { outcome: { kind: 'invalid_response' }, usage: null };
  }

  const { result_summary: summary, usage } = parsed.data;
  return { outcome: toOutcome(summary), usage: usage ?? null };
};

const toOutcome = (
  summary: z.infer<typeof ResultSummarySchema> | null | undefined,
): AiGenerateDockOutcome => {
  if (!summary) return { kind: 'not_generated' };

  const created = Math.min(summary.created_count, summary.requested_count);
  if (created === 0) return { kind: 'failed' };
  if (created < summary.requested_count) {
    return {
      kind: 'partial',
      created,
      failed: summary.requested_count - created,
    };
  }
  return { kind: 'success', created };
};

const ErrorEventSchema = z
  .object({
    type: z.literal('error'),
    code: z.string(),
    reason: z.string().nullish(),
    retry_after_seconds: z.number().nullish(),
    field: z.string().nullish(),
    usage: UsageSchema.nullish(),
  })
  .passthrough();

export type AiGenerateDockErrorOutcome =
  | { kind: 'failed' }
  | { kind: 'quota_exceeded'; usage: AiGenerateDockUsage | null }
  | { kind: 'rate_limited' | 'cooldown'; retryAfterSeconds: number | null }
  | { kind: 'invalid_request'; field: string | null }
  | { kind: 'session_locked' }
  | { kind: 'auth_expired' }
  | { kind: 'other'; code: string | null };

const USAGE_BLOCKED_REASONS = new Set(['rate_limited', 'cooldown']);

/**
 * 생성기 턴을 끝낸 error 이벤트를 분기로 바꾼다. 스트림 전 HTTP 거절도 transport 가 같은
 * error 이벤트로 펴서 넘기므로 한 곳에서 읽는다.
 * 생성 0건은 respond 없이 `error{GENERATION_EMPTY_RESULT}` 로 끝난다 — 전체 실패의 정본.
 */
export const resolveAiGenerateDockErrorEvent = (
  event: unknown,
): AiGenerateDockErrorOutcome => {
  const parsed = ErrorEventSchema.safeParse(event);
  if (!parsed.success) return { kind: 'other', code: null };

  const { code, reason, retry_after_seconds, field, usage } = parsed.data;
  switch (code) {
    case 'GENERATION_EMPTY_RESULT':
      return { kind: 'failed' };
    case 'DIRECT_GENERATE_INVALID':
      return { kind: 'invalid_request', field: field ?? null };
    case 'SESSION_LOCKED':
      return { kind: 'session_locked' };
    case 'AUTH_EXPIRED':
      return { kind: 'auth_expired' };
    case 'USAGE_BLOCKED':
      break;
    default:
      return { kind: 'other', code };
  }

  if (reason && USAGE_BLOCKED_REASONS.has(reason)) {
    return {
      kind: reason as 'rate_limited' | 'cooldown',
      retryAfterSeconds: retry_after_seconds ?? null,
    };
  }
  // reason 이 없거나 모르는 값이면 소진으로 본다 — 다시 눌러도 같은 결과다.
  return { kind: 'quota_exceeded', usage: usage ?? null };
};
